import { classNames } from '../utils/UNSAFE_classNames';
import { ComponentChildren, JSX } from 'preact';
import { Flex } from '../UNSAFE_Flex';
import { useFormContext } from '../hooks/UNSAFE_useFormContext';
import { useFormFieldContext } from '../hooks/UNSAFE_useFormFieldContext';
import { sizeToCSS, Size } from '../utils/UNSAFE_size';
import { labelSlotStyles, labelInnerStyles, valueSlotStyles } from './LabelValueLayoutStyles.css';
import { useComponentTheme } from '../hooks/UNSAFE_useComponentTheme';
import { TextFieldRedwoodTheme } from '../UNSAFE_TextField/themes/redwood/TextFieldTheme';
import { RadioRedwoodTheme } from '../UNSAFE_RadioItem/themes/redwood/RadioTheme';
import { LabelRedwoodTheme } from '../UNSAFE_Label/themes/redwood/LabelTheme';
import { FormLayoutRedwoodTheme } from '../UNSAFE_TextField/themes/redwood/FormLayoutTheme';
import { CheckboxRedwoodTheme } from '../UNSAFE_Checkbox/themes/redwood/CheckboxTheme';

type TextFieldProps = {
  /**
   * Defines how the label is going to be positioned. For a TextField only the start and top values are allowed.
   */
  labelEdge?: 'start' | 'top';
  parentComponentVariant?: 'textField';
};
type CheckboxRadioSetProps = {
  /**
   * Defines how the label is going to be positioned. For CheckboxSet or RadioSet the start, top and inside values are supported.
   */
  labelEdge?: 'start' | 'top' | 'inside';
  parentComponentVariant: 'radioSet' | 'checkboxSet';
};

type Props = (TextFieldProps | CheckboxRadioSetProps) & {
  /**
   * Label content of the layout
   */
  label?: ComponentChildren;

  /**
   * Defines the label width for labelEdge 'start' ('top' is always 100%)
   */
  labelStartWidth?: Size;
  /**
   * Value content of the layout
   */
  children: ComponentChildren;

  hasTopUserAssistance?: boolean;
};

export const LabelValueLayout = ({
  label,
  labelEdge,
  children,
  labelStartWidth = '33%',
  parentComponentVariant = 'textField',
  hasTopUserAssistance = false
}: Props) => {
  const { baseTheme: textFieldTheme } = useComponentTheme(TextFieldRedwoodTheme);
  const { baseTheme: radioTheme } = useComponentTheme(RadioRedwoodTheme);
  const { baseTheme: checkboxTheme } = useComponentTheme(CheckboxRedwoodTheme);
  const { baseTheme: labelTheme } = useComponentTheme(LabelRedwoodTheme);
  const { baseTheme: formLayoutTheme } = useComponentTheme(FormLayoutRedwoodTheme);

  const isTextField = parentComponentVariant === 'textField';
  const isRadio = parentComponentVariant === 'radioSet';
  const isCheckbox = parentComponentVariant === 'checkboxSet';
  const isStart = labelEdge === 'start';
  const isTop = labelEdge === 'top' || labelEdge === 'inside'; // 'inside' needs to pick up all 'top' styles.
  const isInside = labelEdge === 'inside';
  const width = sizeToCSS(labelStartWidth);
  const valueWidth = `calc(100% - ${width})`;

  const labelStyles: JSX.CSSProperties = isStart
    ? { flexBasis: width, width: width, maxWidth: width }
    : {};

  const valueStyles: JSX.CSSProperties = isStart
    ? { flexBasis: valueWidth, width: valueWidth, maxWidth: valueWidth }
    : {};

  const { isFormLayout, isReadonly: isFormReadonly, labelWrapping } = useFormContext();
  const { isReadonly } = useFormFieldContext();
  const isPureReadonly = (isFormLayout && isFormReadonly) || (!isFormLayout && isReadonly);

  const labelStyleClasses = classNames([
    formLayoutTheme,
    labelSlotStyles.base,
    isStart && labelSlotStyles.start,
    isTop && labelSlotStyles.top,
    isTop && hasTopUserAssistance && labelSlotStyles.topAndTopUA,
    isTop && isPureReadonly && labelSlotStyles.topPureReadonly
  ]);

  const labelInnerClasses = classNames([
    isTextField && textFieldTheme,
    isRadio && radioTheme,
    isCheckbox && checkboxTheme,
    (isRadio || isCheckbox) && labelTheme,
    formLayoutTheme,
    labelInnerStyles.base,
    isStart && labelInnerStyles.start,
    isTextField && isStart && !isPureReadonly && labelInnerStyles.startNotPureReadonly,
    isRadio && isInside && labelInnerStyles.insideRadioSet,
    isCheckbox && isInside && labelInnerStyles.insideCheckboxSet,
    isRadio && isStart && !hasTopUserAssistance && labelInnerStyles.startWithNoTopRadioSetUA,
    isCheckbox && isStart && !hasTopUserAssistance && labelInnerStyles.startWithNoTopCheckboxSetUA,
    labelWrapping === 'truncate' && labelInnerStyles.noWrap
  ]);

  return (
    <Flex wrap="wrap" align="start">
      <div class={labelStyleClasses} style={labelStyles}>
        <div class={labelInnerClasses}>{label}</div>
      </div>
      <div class={valueSlotStyles} style={valueStyles}>
        {children}
      </div>
    </Flex>
  );
};
